import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ListChecks, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@tes/ui';
import { api } from '../../lib/api';
import { messageOf } from '../../lib/errors';
import type { VariableGroupDef } from '../../lib/types';
import { GroupFormDialog } from './GroupFormDialog';
import { useVariableGroups } from './useVariables';

/**
 * Choice groups: several checkbox variables that form ONE mutually-exclusive
 * question (e.g. which retailer). Lives under the variable table because
 * membership is set on the variables themselves.
 */
export function ChoiceGroupsSection() {
  const qc = useQueryClient();
  const { data: groups, isLoading } = useVariableGroups();
  const [editing, setEditing] = useState<VariableGroupDef | null>(null);
  const [adding, setAdding] = useState(false);

  const remove = useMutation({
    mutationFn: (g: VariableGroupDef) => api.deleteVariableGroup(g.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['variable-groups'] }),
  });

  return (
    <div className="mt-6">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
            <ListChecks size={15} /> Choice groups
          </h3>
          <p className="text-xs text-slate-400">
            One question, several checkboxes — the person picks exactly one member; creation
            enforces it.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
          <Plus size={14} className="mr-1" /> Add group
        </Button>
      </div>

      {remove.error && (
        <p className="mb-2 text-sm text-red-600">{messageOf(remove.error, 'Delete failed')}</p>
      )}

      {isLoading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : (groups ?? []).length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-400">
          No groups yet. Create one, then assign checkbox variables to it in their edit dialog.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {(groups ?? []).map((g) => (
            <li key={g.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <span className="font-mono text-xs">{g.key}</span>
                <span className="mx-2 text-slate-300">·</span>
                <span className="text-slate-700">{g.prompt}</span>
                <p className="truncate text-xs text-slate-400">
                  {g.members.length
                    ? `Members: ${g.members.join(', ')}`
                    : 'No members yet — assign checkbox variables to this group'}
                </p>
              </div>
              {g.required ? (
                <span className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-600">
                  required
                </span>
              ) : (
                <span className="text-xs text-slate-400">optional</span>
              )}
              <button
                type="button"
                onClick={() => setEditing(g)}
                className="rounded p-1 text-slate-500 hover:bg-slate-100"
                aria-label={`Edit ${g.key}`}
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                onClick={() => remove.mutate(g)}
                disabled={remove.isPending}
                className="rounded p-1 text-red-500 hover:bg-red-50"
                aria-label={`Delete ${g.key}`}
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {(adding || editing) && (
        <GroupFormDialog initial={editing} onClose={() => (setAdding(false), setEditing(null))} />
      )}
    </div>
  );
}
